import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  ActionButton,
  Content,
  Dialog,
  DialogTrigger,
  Item,
  ListView,
  SearchField,
} from "@adobe/react-spectrum";
import { RootState } from "../../store/storeConfig";
import { ChevronDown, UserGroup } from "../common/Icons";
import { AssignQueues } from "../../types/agent.type";
import Emitter from "../../emitter";
import { API_QUEUE_JOIN_UPDATE } from "../../constants/events";
import "./style.css";
import { tabAccessControl } from "../../utils/utils";

const QueueSelection: React.FC = () => {
  const agent = useSelector((state: RootState) => state.agent);
  const webrtc = useSelector((state: RootState) => state.webrtc);

  const [isQueueDialogOpen, setIsQueueDialogOpen] = useState(false);
  const [currentStateQueues, setCurrentStateQueues] = useState<Set<any>>(
    new Set()
  );
  const [allQueueIds, setAllQueueIds] = useState<Set<any>>(new Set());
  const [disabledQueueIds, setDisabledQueueIds] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [filteredSearch, setFilteredSearch] = useState<any[]>([]);

  const queuesToUpdate = useRef<{ id: any; joined: boolean }[]>([]);

  // Initialize queue states from agent
  const updateCurrentStateQueues = () => {
    const entities = agent?.queuesAssigned?.entities || [];
    const joined = new Set(
      entities.filter((q: any) => q?.joined).map((q: any) => q.id)
    );
    const all = new Set(entities.map((q: any) => q.id));
    setCurrentStateQueues(joined);
    setAllQueueIds(all);
  };

  useEffect(() => {
    updateCurrentStateQueues();
  }, []);

  // Trigger sync when dialog opens
  useEffect(() => {
    if (isQueueDialogOpen) {
      updateCurrentStateQueues();
      setSearchValue("");
      setFilteredSearch(
        getUniqueObjectsOnly(agent?.queuesAssigned?.entities, "id")
      );
    }
  }, [isQueueDialogOpen]);

  // Handle WebRTC state
  useEffect(() => {
    if (!webrtc.isVoiceNetworkAvailable) {
      const disabled =
        agent?.queuesAssigned?.entities
          ?.filter((q: AssignQueues) =>
            q.channel?.toLowerCase().includes("voice")
          )
          .map((q: any) => q.id) || [];
      setDisabledQueueIds(disabled);
    } else {
      setDisabledQueueIds([]);
    }
  }, [webrtc.isVoiceNetworkAvailable]);

  // Queue selection change handler
  const updateSelectedQueues = (newSelected: Set<any>) => {
    const oldSet = currentStateQueues;
    const added = [...newSelected]
      .filter((id) => !oldSet.has(id))
      .map((id) => ({ id, joined: true }));
    const removed = [...oldSet]
      .filter((id) => !newSelected.has(id))
      .map((id) => ({ id, joined: false }));
    
    // Create a map to deduplicate and ensure latest state for each queue
    const updateMap = new Map();
    
    // Add existing pending updates
    queuesToUpdate.current.forEach(update => {
      updateMap.set(update.id, update);
    });
    
    // Add new updates, overwriting any existing ones for the same queue
    [...added, ...removed].forEach(update => {
      updateMap.set(update.id, update);
    });
    
    // Convert map back to array
    queuesToUpdate.current = Array.from(updateMap.values());
    setCurrentStateQueues(new Set(newSelected));
  };

  // Debounce send to background
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (queuesToUpdate.current.length > 0) {
        Emitter.emit(API_QUEUE_JOIN_UPDATE, {
          queueJoiningList: queuesToUpdate.current,
        });
        queuesToUpdate.current = [];
      }
    }, 3000);
    return () => clearTimeout(timeout);
  }, [currentStateQueues]);

  // Search filtering
  const handleSearch = (value: string) => {
    setSearchValue(value);
    const list = getUniqueObjectsOnly(agent?.queuesAssigned?.entities, "id");
    const filtered = list.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredSearch(filtered);
  };

  const getUniqueObjectsOnly = (arr: any[], key: string) => {
    return [...new Map((arr || []).map((item) => [item[key], item])).values()];
  };

  return (
    <div className="openStatusPanelIcon">
      {!!agent?.queueSelectionAllowed &&
        agent.queueSelectionAllowed !== "false" &&
        tabAccessControl("queue_selection") === "true" && (
          <DialogTrigger type="popover" hideArrow placement="bottom end">
            <ActionButton
              UNSAFE_className="queueSelectionButton"
              onPress={() => setIsQueueDialogOpen(true)}
            >
              <div className="selectQueueButton">
                <span>
                  <UserGroup /> Queues
                </span>
                <span>
                  {currentStateQueues.size > 0
                    ? `${currentStateQueues.size} Selected`
                    : ""}
                  <ChevronDown />
                </span>
              </div>
            </ActionButton>

            <Dialog size="S" width="360px" height="450px">
              <Content UNSAFE_className="queue-selection-dialog">
                <SearchField
                  width="100%"
                  value={searchValue}
                  onClear={() => handleSearch("")}
                  onChange={(val) => handleSearch(val)}
                  necessityIndicator="icon"
                  marginBottom="15px"
                  UNSAFE_className="qsearchField"
                  aria-label="qsearchField"
                />
                <ListView
                  items={
                    searchValue.length > 0
                      ? filteredSearch
                      : getUniqueObjectsOnly(
                          agent?.queuesAssigned?.entities,
                          "id"
                        )
                  }
                  selectedKeys={currentStateQueues}
                  disabledKeys={
                    agent.queueSelectionMode === "view"
                      ? allQueueIds
                      : new Set(disabledQueueIds)
                  }
                  selectionMode="multiple"
                  selectionStyle="checkbox"
                  aria-label="queues"
                  isQuiet
                  minWidth="size-3400"
                  onSelectionChange={(keys) =>
                    updateSelectedQueues(new Set(keys))
                  }
                  UNSAFE_className="queues-list"
                >
                  {(item) => <Item key={item.id}>{item.name}</Item>}
                </ListView>
              </Content>
            </Dialog>
          </DialogTrigger>
        )}
    </div>
  );
};

export default QueueSelection;
